import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  ArrowLeft, CheckSquare, CheckCircle, XCircle, Clock,
  AlertCircle, ChevronDown, ChevronUp, Save, User, Calendar,
  Camera, MessageSquare, Plus, Eye, Trash2, Link2, Ship
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QAGates = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [expandedGate, setExpandedGate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    batch_id: '',
    product_name: '',
    dossier_id: '',
    shipment_id: ''
  });

  useEffect(() => {
    if (isNew) {
      fetchDossiers();
      fetchShipments();
    } else if (id) {
      fetchRecord(id);
    } else {
      fetchAllRecords();
    }
  }, [id, isNew]);

  const fetchShipments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/shipments`);
      const data = await response.json();
      setShipments(data.shipments || []);
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  const fetchDossiers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/dossiers`);
      const data = await response.json();
      setDossiers(data.dossiers || []);
    } catch (error) {
      console.error('Error fetching dossiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRecords = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/qa-gates`);
      const data = await response.json();
      setRecords(data.records || []);
    } catch (error) {
      console.error('Error fetching QA records:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecord = async (recordId) => {
    try {
      const response = await fetch(`${API_URL}/api/eximstack/qa-gates/${recordId}`);
      const data = await response.json();
      setSelectedRecord(data);
    } catch (error) {
      console.error('Error fetching QA record:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateChecklistItem = (gateIndex, itemIndex, field, value) => {
    const updated = { ...selectedRecord };
    updated.gates[gateIndex].checklist[itemIndex][field] = value;
    setSelectedRecord(updated);
  };

  const updateGateStatus = async (gateIndex) => {
    const gate = selectedRecord.gates[gateIndex];
    const allChecked = gate.checklist.every(item => item.result && item.result !== 'Pending');
    const anyFailed = gate.checklist.some(item => item.result === 'Fail');
    
    const newStatus = anyFailed ? 'Failed' : (allChecked ? 'Passed' : 'In Progress');
    
    const updated = { ...selectedRecord };
    updated.gates[gateIndex].overall_status = newStatus;
    updated.gates[gateIndex].inspection_date = new Date().toISOString().split('T')[0];
    setSelectedRecord(updated);

    setSaving(true);
    try {
      await fetch(`${API_URL}/api/eximstack/qa-gates/${selectedRecord.id}/gate/${gate.gate_number}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checklist: gate.checklist,
          overall_status: newStatus,
          inspection_date: updated.gates[gateIndex].inspection_date
        })
      });
      toast({ title: 'Saved', description: `Gate ${gate.gate_number} updated successfully` });
    } catch (error) {
      console.error('Error updating gate:', error);
      toast({ title: 'Error', description: 'Failed to update gate', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDossierSelect = (dossierId) => {
    const selected = dossiers.find(d => d.id === dossierId);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        batch_id: selected.batch_id,
        product_name: selected.product_name,
        dossier_id: dossierId,
        shipment_id: selected.shipment_id || prev.shipment_id
      }));
    }
  };

  const handleShipmentSelect = (shipmentId) => {
    const selected = shipments.find(s => s.id === shipmentId);
    if (selected) {
      setFormData(prev => ({
        ...prev,
        product_name: selected.product_name || prev.product_name,
        batch_id: selected.batch_number || prev.batch_id,
        shipment_id: shipmentId
      }));
    } else {
      setFormData(prev => ({ ...prev, shipment_id: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/eximstack/qa-gates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast({ title: 'Success', description: 'QA record created with default checklist!' });
        navigate(`/eximstack/qa-gates/${result.record.id}`);
      } else {
        throw new Error('Failed to create QA record');
      }
    } catch (error) {
      console.error('Error creating QA record:', error);
      toast({ title: 'Error', description: 'Failed to create QA record', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this QA record?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/eximstack/qa-gates/${recordId}`, { method: 'DELETE' });
      if (response.ok) {
        toast({ title: 'Deleted', description: 'QA record deleted successfully' });
        fetchAllRecords();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete QA record', variant: 'destructive' });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Passed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'Failed': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'In Progress': return <Clock className="w-5 h-5 text-blue-500" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Passed': return 'bg-green-100 text-green-800';
      case 'Failed': return 'bg-red-100 text-red-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getGateProgress = (gate) => {
    const total = gate.checklist.length;
    const completed = gate.checklist.filter(item => item.result && item.result !== 'Pending').length;
    return Math.round((completed / total) * 100);
  };

  // List View
  if (!id && !isNew) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <Link to="/eximstack" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-2">
                <ArrowLeft className="w-4 h-4" /> Back to EximStack
              </Link>
              <h1 className="text-3xl font-bold text-[#263776]">QA Gates Records</h1>
              <p className="text-gray-600">Stage-wise quality inspection records for all batches</p>
            </div>
            <Link to="/eximstack/qa-gates/new">
              <Button className="bg-[#2196F3] hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> New QA Record
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : records.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No QA Records Yet</h3>
                <p className="text-gray-500 mb-4">Create QA records to track quality at each processing stage</p>
                <Link to="/eximstack/qa-gates/new">
                  <Button className="bg-[#2196F3]">
                    <Plus className="w-4 h-4 mr-2" /> Create QA Record
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {records.map((record) => {
                const completedGates = record.gates.filter(g => g.overall_status === 'Passed').length;
                const totalGates = record.gates.length;
                
                return (
                  <Card key={record.id} className="hover:shadow-lg transition-shadow h-full" data-testid={`qa-record-card-${record.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <Badge className={getStatusBadge(record.overall_status)}>
                            {record.overall_status}
                          </Badge>
                        </div>
                        <CardTitle className="text-lg">{record.product_name}</CardTitle>
                        <p className="text-sm text-gray-500 font-mono">{record.batch_id}</p>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span>Progress</span>
                            <span>{completedGates}/{totalGates} Gates</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${(completedGates / totalGates) * 100}%` }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mb-4">
                          {record.gates.map((gate, idx) => (
                            <div 
                              key={idx}
                              className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium ${
                                gate.overall_status === 'Passed' ? 'bg-green-100 text-green-800' :
                                gate.overall_status === 'Failed' ? 'bg-red-100 text-red-800' :
                                gate.overall_status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-600'
                              }`}
                              title={gate.gate_name}
                            >
                              {gate.gate_number}
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 pt-3 border-t">
                          <Link to={`/eximstack/qa-gates/${record.id}`} className="flex-1">
                            <Button variant="outline" size="sm" className="w-full">
                              <Eye className="w-4 h-4 mr-1" /> Inspect
                            </Button>
                          </Link>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => deleteRecord(record.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Create New Form
  if (isNew) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <Link to="/eximstack/qa-gates" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to QA Records
          </Link>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-[#263776] flex items-center gap-2">
                <CheckSquare className="w-6 h-6 text-[#2196F3]" />
                Create QA Record
              </CardTitle>
              <p className="text-gray-600">Initialize quality gates for a batch</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Shipment Linking */}
                {shipments.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Ship className="w-5 h-5 text-green-600" />
                      <Label className="font-semibold text-green-800">Link to Shipment (Optional)</Label>
                    </div>
                    <select 
                      className="w-full border border-green-300 rounded-md p-2 bg-white"
                      value={formData.shipment_id}
                      onChange={(e) => handleShipmentSelect(e.target.value)}
                    >
                      <option value="">-- Select shipment --</option>
                      {shipments.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.batch_number} - {s.product_name} ({s.shipment_type}: {s.origin} → {s.destination})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-green-600 mt-2">
                      Selecting a shipment will auto-fill product and batch information.
                    </p>
                  </div>
                )}

                {dossiers.length > 0 && (
                  <div>
                    <Label>Link to Dossier (Optional)</Label>
                    <select 
                      className="w-full border rounded-md p-2 mt-1"
                      onChange={(e) => handleDossierSelect(e.target.value)}
                    >
                      <option value="">-- Select existing dossier --</option>
                      {dossiers.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.product_name} ({d.batch_id})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="grid gap-4">
                    <div>
                      <Label>Batch ID *</Label>
                      <Input 
                        required
                        placeholder="e.g., BATCH-20241215-ABC123"
                        value={formData.batch_id}
                        onChange={(e) => setFormData({ ...formData, batch_id: e.target.value })}
                        data-testid="batch-id-input"
                      />
                    </div>
                    <div>
                      <Label>Product Name *</Label>
                      <Input 
                        required
                        placeholder="e.g., Premium Turmeric Fingers"
                        value={formData.product_name}
                        onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                        data-testid="product-name-input"
                      />
                    </div>
                  </div>
                </div>

                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <CheckSquare className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-[#263776]">6-Stage QA Checklist</h4>
                        <p className="text-sm text-gray-600">
                          The record will be created with a comprehensive checklist covering:
                          Pre-Harvest, Post-Harvest, Processing, Pre-Packaging, Pre-Shipment, and Arrival inspections.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-end gap-4 pt-4 border-t">
                  <Link to="/eximstack/qa-gates">
                    <Button type="button" variant="outline">Cancel</Button>
                  </Link>
                  <Button type="submit" className="bg-[#2196F3]" disabled={saving} data-testid="create-qa-record-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Creating...' : 'Create QA Record'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Detail View
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!selectedRecord) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-4">QA Record not found</h2>
          <Link to="/eximstack/qa-gates">
            <Button>Back to QA Gates</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <Link to="/eximstack/qa-gates" className="text-sm text-gray-500 hover:text-[#263776] flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to QA Records
        </Link>

        {/* Header */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <Badge className={getStatusBadge(selectedRecord.overall_status) + ' mb-2'}>
                  {selectedRecord.overall_status}
                </Badge>
                <h1 className="text-2xl font-bold text-[#263776]">{selectedRecord.product_name}</h1>
                <p className="text-gray-500 font-mono">{selectedRecord.batch_id}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">
                  Gates Completed: {selectedRecord.gates.filter(g => g.overall_status === 'Passed').length}/{selectedRecord.gates.length}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gates Accordion */}
        <div className="space-y-4">
          {selectedRecord.gates.map((gate, gateIndex) => (
            <Card key={gate.gate_id} className={`overflow-hidden ${
              gate.overall_status === 'Passed' ? 'border-l-4 border-l-green-500' :
              gate.overall_status === 'Failed' ? 'border-l-4 border-l-red-500' :
              gate.overall_status === 'In Progress' ? 'border-l-4 border-l-blue-500' :
              'border-l-4 border-l-gray-300'
            }`}>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setExpandedGate(expandedGate === gateIndex ? null : gateIndex)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      gate.overall_status === 'Passed' ? 'bg-green-100 text-green-800' :
                      gate.overall_status === 'Failed' ? 'bg-red-100 text-red-800' :
                      gate.overall_status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {gate.gate_number}
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        {gate.gate_name}
                        {getStatusIcon(gate.overall_status)}
                      </CardTitle>
                      <p className="text-sm text-gray-500">{gate.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden sm:block">
                      <div className="text-sm font-medium">{getGateProgress(gate)}% Complete</div>
                      <div className="w-24 bg-gray-200 rounded-full h-1.5 mt-1">
                        <div 
                          className={`h-1.5 rounded-full ${
                            gate.overall_status === 'Passed' ? 'bg-green-500' :
                            gate.overall_status === 'Failed' ? 'bg-red-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${getGateProgress(gate)}%` }}
                        />
                      </div>
                    </div>
                    {expandedGate === gateIndex ? 
                      <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    }
                  </div>
                </div>
              </CardHeader>

              {expandedGate === gateIndex && (
                <CardContent className="border-t bg-gray-50">
                  {/* Inspector Info */}
                  {gate.inspector_name && (
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{gate.inspector_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{gate.inspection_date}</span>
                      </div>
                    </div>
                  )}

                  {/* Checklist Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-white">
                          <th className="text-left p-3 font-medium">Check Item</th>
                          <th className="text-left p-3 font-medium w-32">Standard</th>
                          <th className="text-left p-3 font-medium w-28">Result</th>
                          <th className="text-left p-3 font-medium w-32">Actual Value</th>
                          <th className="text-left p-3 font-medium w-40">Comments</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gate.checklist.map((item, itemIndex) => (
                          <tr key={item.item_id} className="border-b hover:bg-white transition-colors">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {item.result === 'Pass' ? (
                                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                ) : item.result === 'Fail' ? (
                                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                ) : (
                                  <AlertCircle className="w-4 h-4 text-gray-300 flex-shrink-0" />
                                )}
                                <span>{item.check_item}</span>
                              </div>
                            </td>
                            <td className="p-3 text-gray-500 text-xs">{item.standard}</td>
                            <td className="p-3">
                              <select
                                className={`w-full border rounded p-1.5 text-sm ${
                                  item.result === 'Pass' ? 'bg-green-50 border-green-200' :
                                  item.result === 'Fail' ? 'bg-red-50 border-red-200' :
                                  'bg-white'
                                }`}
                                value={item.result || ''}
                                onChange={(e) => updateChecklistItem(gateIndex, itemIndex, 'result', e.target.value)}
                              >
                                <option value="">Select</option>
                                <option value="Pass">Pass</option>
                                <option value="Fail">Fail</option>
                                <option value="N/A">N/A</option>
                              </select>
                            </td>
                            <td className="p-3">
                              <Input
                                className="h-8 text-sm"
                                placeholder="Value"
                                value={item.actual_value || ''}
                                onChange={(e) => updateChecklistItem(gateIndex, itemIndex, 'actual_value', e.target.value)}
                              />
                            </td>
                            <td className="p-3">
                              <Input
                                className="h-8 text-sm"
                                placeholder="Comments"
                                value={item.comments || ''}
                                onChange={(e) => updateChecklistItem(gateIndex, itemIndex, 'comments', e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end mt-4 pt-4 border-t">
                    <Button 
                      onClick={() => updateGateStatus(gateIndex)}
                      disabled={saving}
                      className="bg-[#2196F3]"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save & Update Status'}
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QAGates;
